import { OrganizationTypes } from '@/enums';
import { OrganizationStatuses } from '@/enums/statuses/organizations';
import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination,
  BaseResponse,
  Filters,
  FiltersPagination,
  Response,
} from '@/types';
import { del_mask, prepare_formdata } from '@/utils';

import {
  ApiTokenKey,
  CompanyAccount,
  CompanyContract,
  DeliveryAddress,
  ModelContractsFilter,
  Organizations,
  TransportCompany,
} from '../types';

interface ContractsAccounts {
  contracts: CompanyContract[],
  invoices: CompanyAccount[],
  active_contract_count: number,
}

export interface ApiResponseOrganization extends Organizations.UserOrganization {
  organizations: Organizations.Organization[],
  cities: string[],
}

interface OrganizationCheck extends BaseResponse {
  data: {
    items: Organizations.OrganizationCheck[],
  }
}
interface OrganizationBlackList extends BaseResponse, ApiPagination {
  data: {
    items: Organizations.OrganizationBlackList[],
  }
}
interface ModelAccreditation extends BaseResponse, ApiPagination {
  data: {
    items: Organizations.ModelAccreditation[],
    accredited_count: number
  }
}

export class BaseOrganizationsService extends BaseApiService {
  public readonly base_path = 'partner';

  async get_delivery_addresses(
    id: string | number,
  ): Promise<Response<DeliveryAddress[]>> {
    const url = `${this.base_path}/organizations/${id}/delivery_addresses`;
    const { data } = await this.http_client.get<Response<DeliveryAddress[]>>(url);

    return data;
  }

  async get_transport_companies(
    id: string | number,
  ): Promise<Response<TransportCompany[]>> {
    const url = `${this.base_path}/organizations/${id}/transport_companies`;
    const { data } = await this.http_client.get<Response<TransportCompany[]>>(url);

    return data;
  }
}

export class OrganizationsService extends BaseOrganizationsService {
  async get_organizations(): Promise<Response<ApiResponseOrganization>> {
    const url = `${this.base_path}/organizations`;
    const { data } = await this.http_client.get<Response<ApiResponseOrganization>>(url);

    return data;
  }

  async upd_organization(org: Partial<Organizations.Organization>): Promise<number | undefined> {
    const url = `${this.base_path}/organizations/${org.id}`;
    const payload = { ...org };

    payload.telephone = del_mask(payload.telephone);
    payload.inn_organization = del_mask(payload.inn_organization);
    payload.kpp_organization = del_mask(payload.kpp_organization);

    const { data } = await this.http_client.patch<Response<null>>(url, prepare_formdata(payload));

    return data.code;
  }

  async get_partner_service_centers(): Promise<Response<Organizations.ServiceCenter[]>> {
    const url = `${this.base_path}/company/service_centers`;
    const { data } = await this.http_client.get<Response<Organizations.ServiceCenter[]>>(url);

    return data;
  }

  async get_partner_sale_points(): Promise<Response<Organizations.SalePoint[]>> {
    const url = `${this.base_path}/company/sale_points`;
    const { data } = await this.http_client.get<Response<Organizations.SalePoint[]>>(url);

    return data;
  }

  async add_new_sale_point(point: Organizations.SalePoint): Promise<Response<Record<'sale_point_id', number>>> {
    if (point.telephone.length > 1) {
      point.telephone = point.telephone.filter((phone) => phone !== '');
    }

    const { data } = await this.http_client.post<Response<Record<'sale_point_id', number>>>(
      `${this.base_path}/company/sale_points`,
      point,
    );

    return data;
  }

  async get_organization_by_id(id: string | number): Promise<Response<Organizations.Organization>> {
    const url = `${this.base_path}/organizations/${id}`;
    const { data } = await this.http_client.get<Response<Organizations.Organization>>(url);

    return data;
  }

  async add_delivery_address(id: string | number, addr: DeliveryAddress):
    Promise<Response<Record<'delivery_address_id', number>>> {
    const url = `${this.base_path}/organizations/${id}/delivery_addresses`;
    const payload = addr;

    payload.telephone = del_mask(payload.telephone);
    const { data } = await this.http_client.post<Response<Record<'delivery_address_id', number>>>(url, prepare_formdata(payload));

    return data;
  }

  async get_company_contracts_accounts(id: string | number, filters: {filters:ModelContractsFilter}): Promise<Response<ContractsAccounts>> {
    const url = `${this.base_path}/organizations/${id}/invoices_contracts`;
    const { data } = await this.http_client.get<Response<ContractsAccounts>>(url, {
      params: {
        filters: filters.filters,
      },
    });

    return data;
  }

  async get_model_accreditation(id: string | number, filters:FiltersPagination<Filters>): Promise<ModelAccreditation> {
    const url = `${this.base_path}/organizations/${id}/cash_registers`;
    const payload = filters;

    const { data } = await this.http_client.get<ModelAccreditation>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async del_sale_point(id: string | number): Promise<BaseResponse> {
    const url = `${this.base_path}/company/sale_points/${id}`;
    const { data } = await this.http_client.delete<BaseResponse>(url);

    return data;
  }

  async upd_sale_point(point: Organizations.SalePoint): Promise<BaseResponse> {
    if (point.telephone.length > 1) {
      point.telephone = point.telephone.filter((phone) => phone !== '');
    }

    const { data } = await this.http_client.patch<BaseResponse>(
      `${this.base_path}/company/sale_points/${point.id}`,
      point,
    );

    return data;
  }

  async get_organization_check(inn_organization: string): Promise<OrganizationCheck> {
    const url = `/organization-validation?inn_organization=${inn_organization}`;

    const { data } = await this.http_client.get<OrganizationCheck>(url);

    return data;
  }

  async get_organization_black_list(filters: FiltersPagination<Filters>): Promise<OrganizationBlackList> {
    const url = '/organization-validation/black-list';
    const payload = filters;

    const { data } = await this.http_client.get<OrganizationBlackList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async generate_api_token_key(): Promise<ApiTokenKey> {
    const url = `${this.base_path}/company/token`;

    const { data } = await this.http_client.get(url);

    return data.data;
  }

  async change_webhook_url(value: string) {
    const url = `${this.base_path}/company/webhook`;

    const payload = {
      url: value,
    };

    const { data } = await this.http_client.patch<BaseResponse>(url, payload);

    return data;
  }

  async get_webhook_logs(filters: FiltersPagination<Filters>) {
    const url = `${this.base_path}/company/webhook/logs`;
    const payload = filters;

    const { data } = await this.http_client.get(url, {
      params: {
        page: payload.pagination.page,
        limit: payload.pagination.limit,
      },
    });

    return data;
  }

  async getWebhookObjects() {
    const url = `${this.base_path}/company/webhook-objects`;

    const { data } = await this.http_client.get(url);

    return data;
  }

  async storeWebhookObjectBlock(webhookObjectId: number) {
    const url = `${this.base_path}/company/webhook-object-blocks`;

    const { data } = await this.http_client.post(url, {
      webhook_object_id: webhookObjectId,
    });

    return data;
  }

  async deleteWebhookObjectBlock(webhookObjectBlockId: number) {
    const url = `${this.base_path}/company/webhook-object-blocks/${webhookObjectBlockId}`;

    await this.http_client.delete(url);
  }

  async get_contract_by_id(contract_id: number| string) {
    const url = `${this.base_path}/organizations/contracts/${contract_id}`;
    const { data } = await this.http_client.get(url);

    return data;
  }
}

export const base_organization = () => ({
  inn_organization: '',
  kpp_organization: '',
  city: '',
  region: '',
  federal_district: '',
  ip_scan_file: '',
  date_registration_company: '',
  general_manager: '',
  name_organization: '',
  name_signatory: '',
  website: '',
  email_1: '',
  email_2: '',
  email_3: '',
  email_4: '',
  contact_person: '',
  position_of_the_contact_person: '',
  telephone: '',
  legal_address: '',
  actual_address: '',
  attorney_file: '',
});

export const default_org = (): Organizations.Organization => ({
  ...base_organization(),
  code: '',
  organization_type: OrganizationTypes.ORGANIZATION,
  organization_status: {
    name: '',
    slug: OrganizationStatuses.ACCEPTED,
  },
  is_cert: false,
});

export const default_sale_point = (): Organizations.SalePoint => ({
  address: '',
  region: '',
  city: '',
  name_sale_point: '',
  telephone: [''],
  website: '',
  is_service: false,
  email: '',
  // показывать или не показывать на сайте
  is_active: false,
});
